import { getCoreRowModel, getExpandedRowModel } from "@tanstack/react-table"
import type { ExpandedState, OnChangeFn, Row, SortingState } from "@tanstack/react-table"
import type { FC, Ref } from "react"
import { useState } from "react"

import { usePayableApprovalColumns } from "../hooks/usePayableApprovalColumns"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import { expandedCellStyles } from "@/components/Tables/Aeros/shared"
import type { Invoice } from "@/graphql/codegen/graphql"
import { InvoiceRowDetails } from "@/screens/Accounting/subfeatures/Invoices/components/InvoiceRowDetails"

interface PayableApprovalsTableProps {
  data: Invoice[]
  sorting: SortingState
  onSortingChange: OnChangeFn<SortingState>
  loading: boolean
  onScroll: () => void
  containerRef?: Ref<HTMLDivElement>
  onReview?: (row: Invoice) => void
}

export const PayableApprovalsTable: FC<PayableApprovalsTableProps> = ({
  data,
  sorting,
  onSortingChange,
  loading,
  onScroll,
  containerRef,
  onReview,
}) => {
  const columns = usePayableApprovalColumns({ onReview })
  const [expanded, setExpanded] = useState<ExpandedState>({})

  const renderExpandedContent = (row: Row<Invoice>) => <InvoiceRowDetails invoice={row.original} />

  return (
    <AerosBaseTable<Invoice>
      features={{
        table: {
          stickyHeader: true,
        },
        tanstackOptions: {
          data,
          columns,
          state: {
            sorting,
            isLoading: loading,
            expanded,
          },
          manualSorting: true,
          enableSorting: true,
          enableMultiSort: false,
          enableSortingRemoval: false,
          getCoreRowModel: getCoreRowModel(),
          getRowId: (row) => row.invoiceId,
          onSortingChange,
          onExpandedChange: setExpanded,
          getRowCanExpand: () => true,
          getExpandedRowModel: getExpandedRowModel(),
          renderExpandedContent,
        },
      }}
      slotProps={{
        container: {
          ref: containerRef,
          onScroll,
          className: "overflow-auto",
          ...({ "data-testid": "payable-approvals.list.table" } as Record<string, string>),
        },
        bodyRow: {
          className: "hover:bg-gray-50",
        },
        bodyCellExpanded: { className: "bg-primary-100" },
        expandedCell: expandedCellStyles,
      }}
      noDataMessage='No invoices found'
    />
  )
}
